import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ExternalLink, Images } from "lucide-react";
import { Section, SectionTitle, Reveal } from "../components/UIBits";
import { useT } from "../lib/i18n";
import api from "../lib/api";

export default function GalleryPage() {
    const { t } = useT();
    const [imgs, setImgs] = useState([]);
    const [driveUrl, setDriveUrl] = useState("");

    useEffect(() => {
        api.get("/gallery").then(r => setImgs(r.data));
        api.get("/settings").then(r => setDriveUrl((r.data.gallery_drive_url || "").trim())).catch(() => {});
    }, []);

    return (
        <div data-testid="gallery-page">
            <Helmet><title>{`${t("gallery.title_pre")} ${t("gallery.title_accent")} — Défi Ultime`}</title></Helmet>
            <Section>
                <SectionTitle kicker={t("gallery.kicker")} title={t("gallery.title_pre")} accent={t("gallery.title_accent")} />

                {driveUrl && (
                    <Reveal>
                        <a
                            href={driveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="glass-card p-5 sm:p-6 mb-8 border-l-4 border-[#7FD93C] flex items-center gap-4 hover:border-[#4FE0D5] hover:-translate-y-0.5 transition-all group"
                            data-testid="gallery-drive-cta"
                        >
                            <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 flex items-center justify-center bg-[#7FD93C]/15 text-[#7FD93C]">
                                <Images size={30} strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-xl sm:text-2xl tracking-wider uppercase text-white group-hover:text-[#4FE0D5] transition-colors">
                                    {t("gallery.drive_cta_title")}
                                </div>
                                <div className="text-sm text-white/60 mt-0.5 line-clamp-2">
                                    {t("gallery.drive_cta_desc")}
                                </div>
                            </div>
                            <div className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-[#7FD93C] text-black font-display tracking-widest text-sm uppercase angle-tag group-hover:bg-[#4FE0D5] transition-colors shrink-0">
                                {t("gallery.drive_cta_btn")} <ExternalLink size={14} />
                            </div>
                            <ExternalLink size={20} className="sm:hidden text-[#7FD93C] shrink-0" />
                        </a>
                    </Reveal>
                )}

                {imgs.length === 0 ? (
                    <div className="text-white/50">{t("gallery.no_photos")}</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {imgs.map((g, i) => (
                            <Reveal key={g.id} delay={i * 0.04}>
                                <div className="glass-card overflow-hidden aspect-square">
                                    <img
                                        src={g.image_base64}
                                        alt={g.caption || ""}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                        style={{ objectPosition: `50% ${g.image_focal_y ?? 50}%` }}
                                    />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}
